import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import FadeIn from "@/components/base/FadeIn";
import gateLogo from "@/assets/gate-logo.jpg";

const LOGO_URL = "https://storage.readdy-site.link/project_files/8c148353-8b70-4bbb-b6e9-ba9d42f8c8c2/e6b211af-c084-4780-9ef8-1b84928b38d0_compressed_IMG_9262.webp";
const VIDEO_URL = "https://storage.readdy-site.link/project_files/8c148353-8b70-4bbb-b6e9-ba9d42f8c8c2/a5c2d9a6-f418-466e-820e-aa4d7c6bdb11_ScreenRecording_06-19-2026-16-41-26_1.mp4";

const FORM_ACTION = "https://readdy.ai/api/form/d8qq3scjgj4v45gj77sg";

export default function Hero() {
  const [showBooking, setShowBooking] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  useEffect(() => {
    if (showBooking) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showBooking]);

  const handleBookingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();

    try {
      const res = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlEncoded,
      });
      if (res.ok) {
        setFormStatus("success");
        setTimeout(() => {
          setShowBooking(false);
          setFormStatus("idle");
          form.reset();
        }, 2000);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <>
      <section
        id="top"
        className="relative overflow-hidden min-h-screen flex flex-col"
      >
        {/* Video background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>

        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40 z-10" />

        {/* Centered hero content */}
        <div className="relative z-40 flex-1 flex flex-col items-center justify-center px-6 text-center">
          {/* Logo medallion */}
          <FadeIn delay={200}>
            <img
              src={gateLogo}
              alt="Fine Arts Tattoo Collective"
              className="w-64 h-64 md:w-80 md:h-80 lg:w-[26rem] lg:h-[26rem] object-contain rounded-full mb-8"
            />
          </FadeIn>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-background-50 mb-4 tracking-tight">
            Fine Arts Tattoo
          </h1>
          <p className="font-sans text-sm md:text-base text-background-50/80 max-w-lg leading-relaxed mb-8">
            Where design defines experience.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/artist/marcus-aurelius"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-sans text-sm font-medium tracking-wider uppercase hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Meet the Artists
              <i className="ri-arrow-right-line text-sm" />
            </Link>
            <button
              onClick={() => setShowBooking(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/50 text-background-50 font-sans text-sm font-medium tracking-wider uppercase hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              Book Now
            </button>
          </div>
        </div>

        {/* Studio tagline — subtle bottom bar (desktop only) */}
        <div className="hidden md:flex relative z-40 px-10 pb-8 pt-4 items-center gap-6">
          <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-background-50/70 whitespace-nowrap">
            Fine Arts Tattoo Collective
          </span>
          <span className="flex-1 h-[0.5px] bg-background-50/30" />
          <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-background-50/70 whitespace-nowrap">
            109 N Union Ave, Cranford, NJ
          </span>
        </div>
      </section>

      {/* Booking Modal */}
      {showBooking && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowBooking(false);
          }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          <div className="relative bg-background-50 text-foreground-950 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 animate-in">
            <button
              onClick={() => setShowBooking(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer text-foreground-600 hover:text-foreground-950"
            >
              <i className="ri-close-line text-xl" />
            </button>

            <h3 className="font-display text-2xl font-medium mb-1">Book an Appointment</h3>
            <p className="font-sans text-sm text-foreground-600 mb-6">
              Fill out the form below and we&apos;ll get back to you within 24 hours.
            </p>

            {formStatus === "success" ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-accent-700" />
                </div>
                <h4 className="font-display text-xl font-medium mb-2">Thank You!</h4>
                <p className="font-sans text-sm text-foreground-600">
                  Your booking request has been submitted. We&apos;ll be in touch soon.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleBookingSubmit}
                data-readdy-form
                className="space-y-4"
              >
                <div>
                  <label htmlFor="booking-name" className="block font-sans text-xs uppercase tracking-wider text-foreground-500 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    id="booking-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 font-sans text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="booking-email" className="block font-sans text-xs uppercase tracking-wider text-foreground-500 mb-1.5">
                    Email *
                  </label>
                  <input
                    id="booking-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 font-sans text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="you@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="booking-phone" className="block font-sans text-xs uppercase tracking-wider text-foreground-500 mb-1.5">
                    Phone
                  </label>
                  <input
                    id="booking-phone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 font-sans text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                    placeholder="(555) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="booking-date" className="block font-sans text-xs uppercase tracking-wider text-foreground-500 mb-1.5">
                    Preferred Date
                  </label>
                  <input
                    id="booking-date"
                    name="preferred_date"
                    type="date"
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 font-sans text-sm text-foreground-950 focus:outline-none focus:border-accent-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="booking-message" className="block font-sans text-xs uppercase tracking-wider text-foreground-500 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="booking-message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-md border border-background-200 bg-background-50 font-sans text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                    placeholder="Tell us about your tattoo idea or preferred style..."
                  />
                </div>

                {formStatus === "error" && (
                  <p className="font-sans text-sm text-primary-600">
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full py-3 rounded-md bg-accent-500 text-background-50 font-sans text-sm font-medium tracking-wider uppercase hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "submitting" ? "Submitting..." : "Submit Booking"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}